import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertTriangle, 
  X, 
  Trash2, 
  User, 
  CreditCard, 
  FileText,
  Shield
} from 'lucide-react';

const EmployeeDeletionModal = ({ isOpen, onClose, onConfirm, employee, loading = false }) => {
  const [confirmText, setConfirmText] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  if (!employee) return null;

  const handleConfirm = () => {
    if (confirmText === 'DELETE' && acknowledged) {
      onConfirm();
    }
  };

  const handleClose = () => {
    setConfirmText('');
    setAcknowledged(false);
    onClose();
  };

  const isConfirmEnabled = confirmText === 'DELETE' && acknowledged;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={handleClose}
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
            >
              {/* Header */}
              <div className="bg-red-50 border-b border-red-200 p-6">
                <div className="flex items-center">
                  <div className="bg-red-100 rounded-full p-3 mr-4">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-red-900">
                      Delete Employee
                    </h3>
                    <p className="text-sm text-red-600 mt-1">
                      This action cannot be undone
                    </p>
                  </div>
                  <button
                    onClick={handleClose}
                    className="ml-auto p-1 text-red-400 hover:text-red-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Employee Info */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="flex items-center mb-2">
                    <User className="w-4 h-4 text-gray-600 mr-2" />
                    <span className="font-medium text-gray-900">
                      {employee.full_name}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <div>Employee Code: <span className="font-medium">{employee.emp_code}</span></div>
                    <div>Department: <span className="font-medium">{employee.department || 'N/A'}</span></div>
                    <div>Status: <span className="font-medium capitalize">{employee.status}</span></div>
                  </div>
                </div>

                {/* Warnings */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-start">
                    <Shield className="w-4 h-4 text-orange-500 mr-2 mt-0.5" />
                    <div className="text-sm text-gray-700">
                      The employee will be <strong>permanently removed</strong> from the system
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <FileText className="w-4 h-4 text-blue-500 mr-2 mt-0.5" />
                    <div className="text-sm text-gray-700">
                      Related profile, documents, attendance, leave, loans, and payroll rows for this employee will also be deleted
                    </div>
                  </div>

                  {employee.has_user_account && (
                    <div className="flex items-start">
                      <CreditCard className="w-4 h-4 text-purple-500 mr-2 mt-0.5" />
                      <div className="text-sm text-gray-700">
                        The linked <strong>login account will be deleted</strong> as well
                      </div>
                    </div>
                  )}

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="text-sm text-yellow-800">
                      <strong>Note:</strong> System administrator accounts (ADMIN*) cannot be deleted. This action cannot be undone.
                    </div>
                  </div>
                </div>

                {/* Acknowledgment */}
                <div className="mb-4">
                  <label className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={acknowledged}
                      onChange={(e) => setAcknowledged(e.target.checked)}
                      className="mt-1 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                    />
                    <span className="text-sm text-gray-700">
                      I understand that this action will permanently delete the employee record 
                      and acknowledge the consequences listed above.
                    </span>
                  </label>
                </div>

                {/* Confirmation Input */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type <code className="bg-gray-100 px-2 py-1 rounded text-red-600 font-mono">DELETE</code> to confirm:
                  </label>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Type DELETE here"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    disabled={loading}
                  />
                </div>

                {/* Actions */}
                <div className="flex space-x-3">
                  <button
                    onClick={handleClose}
                    disabled={loading}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={!isConfirmEnabled || loading}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Employee
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EmployeeDeletionModal;